/**
 * The design's glyph set, inlined. Every path is copied verbatim from the Claude Design so
 * stroke weights and optical sizes match; nothing is pulled from an icon package, which keeps
 * the bundle free of a tree-shaking dependency for ~25 shapes.
 */

import { useI18n } from '../lib/i18n';

export type IconName =
  | 'search'
  | 'chevron'
  | 'chevronDown'
  | 'close'
  | 'expand'
  | 'heart'
  | 'copy'
  | 'share'
  | 'star'
  | 'play'
  | 'lock'
  | 'plus'
  | 'image'
  | 'send'
  | 'reply'
  | 'check'
  | 'globe'
  | 'eye'
  | 'bell'
  | 'trophy'
  | 'upload'
  | 'chat'
  | 'sparkle'
  | 'home'
  | 'user'
  | 'dots'
  | 'download'
  | 'view'
  | 'trash';

interface Shape {
  d: string[];
  /** Filled glyphs (play) draw with fill instead of stroke. */
  filled?: boolean;
  /** Points along the reading direction — mirrored in RTL. */
  directional?: boolean;
  width?: number;
}

const SHAPES: Record<IconName, Shape> = {
  search: { d: ['M11 18a7 7 0 1 1 0-14 7 7 0 0 1 0 14z', 'M16.2 16.2 21 21'] },
  chevron: { d: ['m9.5 5 7 7-7 7'], directional: true, width: 2.2 },
  chevronDown: { d: ['m6 9.5 6 6 6-6'], width: 2.4 },
  close: { d: ['m6.5 6.5 11 11M17.5 6.5l-11 11'], width: 2.6 },
  // Four corner brackets — the universal "open fullscreen", and not directional, so it reads
  // the same in RTL without mirroring.
  expand: {
    d: ['M9 4H4v5', 'M15 4h5v5', 'M9 20H4v-5', 'M15 20h5v-5'],
    width: 2.2,
  },
  heart: { d: ['M12 20.4 4.4 12.8a4.8 4.8 0 0 1 6.8-6.8l.8.8.8-.8a4.8 4.8 0 0 1 6.8 6.8z'] },
  copy: {
    d: [
      'M9.5 8.5h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-9a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2z',
      'M5.5 15.5h-1a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1',
    ],
    width: 1.9,
  },
  share: {
    d: ['M12 16V3.5', 'm8 7.5 4-4 4 4', 'M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6'],
  },
  star: { d: ['m12 3.4 2.7 5.5 6 .9-4.3 4.2 1 6-5.4-2.8-5.4 2.8 1-6L3.3 9.8l6-.9z'], width: 1.6 },
  play: { d: ['M8 4.8 19.5 12 8 19.2z'], filled: true },
  lock: { d: ['M6 11h12v10H6z', 'M9 11V8a3 3 0 0 1 6 0v3'] },
  plus: { d: ['M12 5v14M5 12h14'], width: 2.2 },
  image: {
    d: [
      'M4 5h16v14H4z',
      'M8.6 11.2a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2z',
      'm5.5 17.5 4.5-5 3.5 3.5 2.5-2.5 4 4',
    ],
    width: 1.9,
  },
  send: { d: ['M4 12 20.5 4.5 13 21l-2.6-6.4z'], directional: true, width: 1.9 },
  reply: { d: ['m9.5 6-5 5 5 5', 'M4.5 11H13a6 6 0 0 1 6 6v2'], directional: true },
  check: { d: ['m5 13 4.5 4.5L19.5 7'], width: 2.4 },
  globe: {
    d: [
      'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z',
      'M3.3 12h17.4',
      'M12 3c2.6 2.5 2.6 15.5 0 18M12 3c-2.6 2.5-2.6 15.5 0 18',
    ],
    width: 1.9,
  },
  eye: {
    d: [
      'M12 5.5c5 0 9 4.3 9 6.5s-4 6.5-9 6.5-9-4.3-9-6.5 4-6.5 9-6.5z',
      'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    ],
    width: 1.9,
  },
  bell: { d: ['M18 16.5V10a6 6 0 1 0-12 0v6.5L4 19.5h16z', 'M10 22h4'], width: 1.9 },
  trophy: {
    d: [
      'M8 4h8v4.5a4 4 0 0 1-8 0z',
      'M8 5.5H5V8a3 3 0 0 0 3 3M16 5.5h3V8a3 3 0 0 1-3 3',
      'M12 12.5V17M8.5 21h7',
    ],
    width: 1.8,
  },
  upload: {
    d: [
      'M12 16.5V4',
      'm8 8 4-4 4 4',
      'M4.5 16v3.5a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5V16',
    ],
    width: 1.9,
  },
  chat: {
    d: ['M21 12.5a8 8 0 0 1-8 8H9l-5.5 2.5L5 18.6A8 8 0 0 1 13 4.5a8 8 0 0 1 8 8z'],
    width: 1.9,
  },
  sparkle: { d: ['m12 3 1.9 5.3L19 10l-5.1 1.7L12 17l-1.9-5.3L5 10l5.1-1.7z'], width: 1.9 },
  home: {
    d: ['M3.5 10.6 12 3.5l8.5 7.1V20a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1z', 'M9.5 21v-6h5v6'],
    width: 1.8,
  },
  user: { d: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M4.5 21a7.5 7.5 0 0 1 15 0'], width: 1.8 },
  dots: { d: ['M6 12h.01M12 12h.01M18 12h.01'], width: 2.6 },
  download: { d: ['M12 3.5V16', 'm8 12 4 4 4-4', 'M4.5 19.5h15'], width: 1.9 },
  view: {
    d: [
      'M12 5.5c5 0 9 4.3 9 6.5s-4 6.5-9 6.5-9-4.3-9-6.5 4-6.5 9-6.5z',
      'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    ],
    width: 1.7,
  },
  trash: {
    d: ['M4.5 7h15', 'M9.5 7V5h5v2', 'M6.5 7l1 13h9l1-13', 'M10.5 10.5v6M13.5 10.5v6'],
    width: 1.8,
  },
};

export interface IconProps {
  name: IconName;
  size?: number;
  /** Fill the shape (favourited heart, rated star). */
  fill?: boolean;
  className?: string;
  strokeWidth?: number;
  'aria-hidden'?: boolean;
}

export function Icon({ name, size = 20, fill = false, className, strokeWidth }: IconProps) {
  const { rtl } = useI18n();
  const shape = SHAPES[name];
  const mirrored = shape.directional && rtl;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      style={mirrored ? { transform: 'scaleX(-1)' } : undefined}
      fill={shape.filled || fill ? 'currentColor' : 'none'}
      stroke={shape.filled ? 'none' : 'currentColor'}
      strokeWidth={strokeWidth ?? shape.width ?? 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {shape.d.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
